import React from "react";
import { Box } from "@mui/material";
import ReadOnlyField, { ReadOnlyFieldConfig } from "../ReadOnlyField";

type Props = {
  // Column-major: an array of vertical column stacks (used by most tabs).
  columns?: ReadOnlyFieldConfig[][];
  // Row-major: a flat list that flows left→right, wrapping every 3 columns.
  tiles?: ReadOnlyFieldConfig[];
  openPopperId: string | null;
  setOpenPopperId: React.Dispatch<React.SetStateAction<string | null>>;
};

const TileGrid = ({ columns, tiles, openPopperId, setOpenPopperId }: Props) => {
  // Row-major mode: each tile is a grid cell, auto-flowing across 3 columns.
  if (tiles) {
    return (
      <Box sx={styles.grid}>
        {tiles.map((tile) => (
          <ReadOnlyField
            key={tile.fieldKey}
            {...tile}
            openPopperId={openPopperId}
            setOpenPopperId={setOpenPopperId}
          />
        ))}
      </Box>
    );
  }

  // Column-major mode: each column is a vertical stack in one grid column.
  return (
    <Box sx={styles.grid}>
      {(columns ?? []).map((column, index) => (
        <Box key={index} sx={styles.column}>
          {column.map((tile) => (
            <ReadOnlyField
              key={tile.fieldKey}
              {...tile}
              openPopperId={openPopperId}
              setOpenPopperId={setOpenPopperId}
            />
          ))}
        </Box>
      ))}
    </Box>
  );
};

export default TileGrid;

const styles = {
  grid: {
    display: "grid",
    // minmax(0, 1fr), not 1fr: a bare `1fr` is `minmax(auto, 1fr)`, and that
    // `auto` floor is the item's min-content width. One long unbroken value
    // (a list of dietary restrictions, say) would then stretch its column and
    // squeeze the others, so the tiles came out unequal and never ellipsed.
    // Flooring at 0 keeps the three columns identical and lets the tile's own
    // `text-overflow: ellipsis` do its job.
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: "16px",
    alignItems: "start",
  },
  column: {
    minWidth: 0,
    display: "flex",
    flexDirection: "column" as "column",
    gap: "16px",
  },
};
